// @givmo/app-admin — Employees → Roster: the per-employee OCCASION DATES dialog (EP-WEB11).
//
// One employee's employer-entered birthday (month + day) and hire date, written through
// `PUT /admin/company/employees/{id}/occasion-dates`.
//
// THREE contract details this dialog exists to honour:
//
// 1. PREFILLED FROM THE ROSTER ROW. EP-BE11 put the three fields on `CompanyEmployeeOut`, so the
//    dialog opens showing what is stored without a second request. There is no occasion-dates GET
//    to fall back to, so if the row is stale the roster refetch — not this dialog — is the fix.
//
// 2. ONLY WHAT CHANGED IS SENT. The route reads absent-vs-null: an ABSENT key leaves the stored
//    value alone, an explicit `null` clears it. `buildOccasionDatesBody` therefore writes a key only
//    when it differs from the row, and month/day always travel together (half a birthday is not a
//    date, and the backend 422s the pair). Save is disabled when nothing changed.
//
// 3. NO YEAR EXISTS ANYWHERE IN THE BIRTHDAY PATH. Givmo stores month + day only, and the backend
//    REFUSES a year-carrying value rather than truncating it. Two <Select>s make composing one
//    structurally impossible — there is no text input to type "1990" into and no `Date` built from
//    the pair. (`hire_date` is a genuine full date and uses a real date input.)
//
// A 404 here is AMBIGUOUS and the two readings are opposite. `COMPANY_OCCASION_PROGRAMS_ENABLED`
// gates the backend routes independently of this build's switch (→ the environment is dark), but
// this route also 404s when the employee is not on the company's roster (→ the row on screen is
// stale). `isEmployeeOffRoster` splits them; anything unrecognised falls through to the environment
// reading, which is the conservative one. Neither path raises an error toast.

import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError, sdk, type CompanyEmployeeOut } from '@givmo/api-client';
import { Button, Dialog, Select, TextField, testIdProp, toast } from '@givmo/design-system';
import { employeeFullName } from './employees-decode';
import {
  birthdayDayOptions,
  birthdayMonthOptions,
  buildOccasionDatesBody,
  formatBirthdayLong,
  hireDateError,
  maxHireDate,
  MIN_HIRE_DATE,
  occasionDraftError,
  occasionDraftFromEmployee,
  isEmployeeOffRoster,
  isOccasionRouteUnavailable,
  OCCASION_UNAVAILABLE_NOTE,
  OFF_ROSTER_NOTE,
  type OccasionDatesDraft,
} from './occasion-dates-decode';
import { OccasionUnavailableNotice } from './occasion-unavailable-notice';

const PREFIX = 'roster-occasion';

const GENERIC_ERROR = 'Could not save these dates. Please try again.';

export interface OccasionDatesDialogProps {
  /** The roster row being edited; `null` closes the dialog. */
  employee: CompanyEmployeeOut | null;
  onOpenChange: (open: boolean) => void;
  /** Refetch the roster so the row (and its "Dates on file" cell) shows what was just stored. */
  onSaved: () => void;
  /** A previous occasion write already 404'd — the routes are dark in this environment. */
  unavailable: boolean;
  onUnavailable: () => void;
}

export function OccasionDatesDialog({
  employee,
  onOpenChange,
  onSaved,
  unavailable,
  onUnavailable,
}: OccasionDatesDialogProps) {
  const open = employee !== null;
  const today = useMemo(() => new Date(), []);

  const initial: OccasionDatesDraft = useMemo(
    () =>
      employee === null
        ? { birthdayMonth: '', birthdayDay: '', hireDate: '' }
        : occasionDraftFromEmployee(employee),
    [employee],
  );

  const [draft, setDraft] = useState<OccasionDatesDraft>(initial);
  const [error, setError] = useState<string | null>(null);

  // Re-seed from the row every time the dialog opens on an employee — the prefill IS the read path.
  useEffect(() => {
    setDraft(initial);
    setError(null);
  }, [initial]);

  const save = useMutation({
    mutationFn: (input: {
      id: string;
      body: NonNullable<ReturnType<typeof buildOccasionDatesBody>>;
    }) =>
      sdk.companiesSetCompanyEmployeeOccasionDates({
        path: { employee_user_id: input.id },
        body: input.body,
        throwOnError: true,
      }),
    onSuccess: () => {
      onSaved();
      toast.success('Occasion dates saved');
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      // This route 404s for TWO unrelated reasons (see `isEmployeeOffRoster`), and only one of them
      // is about the environment. A roster miss is about THIS employee — the roster on screen is
      // stale — so it refetches the list (the row will drop out) and explains itself in the dialog,
      // rather than latching the whole surface as unavailable.
      if (isEmployeeOffRoster(err)) {
        onSaved();
        setError(OFF_ROSTER_NOTE);
        return;
      }
      if (isOccasionRouteUnavailable(err)) {
        // Dark backend, not a rejected entry. No toast — the notice replaces the form.
        onUnavailable();
        return;
      }
      setError(isGivmoError(err) ? err.message : GENERIC_ERROR);
    },
  });

  const submitting = save.isPending;
  const draftError = occasionDraftError(draft, today);
  const body = buildOccasionDatesBody(initial, draft);
  const canSave = !submitting && draftError === null && body !== null;

  const submit = () => {
    if (!canSave || employee === null || body === null) return;
    setError(null);
    save.mutate({ id: employee.id, body });
  };

  const setMonth = (value: string) => {
    setError(null);
    setDraft((prev) => {
      // "Not set" on the month clears the whole birthday in one click. Otherwise, a day that does
      // not exist in the newly-picked month is reset rather than silently clamped — the admin
      // re-picks a real day instead of getting a date they did not choose.
      if (value === '') return { ...prev, birthdayMonth: '', birthdayDay: '' };
      const stillValid = birthdayDayOptions(value).some((o) => o.value === prev.birthdayDay);
      return { ...prev, birthdayMonth: value, birthdayDay: stillValid ? prev.birthdayDay : '' };
    });
  };

  const storedBirthday =
    employee === null ? null : formatBirthdayLong(employee.birthday_month, employee.birthday_day);

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <Dialog.Content
        aria-label="Occasion dates"
        className="flex flex-col gap-16"
        testId={`${PREFIX}-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Occasion dates
        </Dialog.Title>

        {unavailable ? (
          <>
            <OccasionUnavailableNotice testId={`${PREFIX}-unavailable`}>
              {OCCASION_UNAVAILABLE_NOTE}
            </OccasionUnavailableNotice>
            <div className="mt-8 flex items-center justify-end">
              <Button
                appearance="saas"
                tone="secondary"
                size="md"
                onClick={() => onOpenChange(false)}
                testId={`${PREFIX}-unavailable-close`}
              >
                Close
              </Button>
            </div>
          </>
        ) : (
          <>
            <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {employee === null ? '' : employeeFullName(employee)} — the dates your company gifting
              programs run on. Givmo stores a birthday as a month and day only; there is no year to
              enter and none is kept.
            </Dialog.Description>

            {/* `placeholder` matches the "Not set" OPTION label on purpose: an empty value renders
                the placeholder, and a trigger reading "Select…" next to an option list whose clear
                row says "Not set" would be two names for one state. */}
            <div className="flex items-end gap-12">
              <div className="flex-1">
                <Select
                  appearance="saas"
                  label="Birthday month"
                  placeholder="Not set"
                  options={birthdayMonthOptions()}
                  value={draft.birthdayMonth}
                  onValueChange={(value) => setMonth(value ?? '')}
                  disabled={submitting}
                  testId={`${PREFIX}-month`}
                />
              </div>
              <div className="w-[112px]">
                <Select
                  appearance="saas"
                  label="Day"
                  placeholder="Not set"
                  options={birthdayDayOptions(draft.birthdayMonth)}
                  value={draft.birthdayDay}
                  onValueChange={(value) => {
                    setError(null);
                    setDraft((prev) => ({ ...prev, birthdayDay: value ?? '' }));
                  }}
                  disabled={submitting}
                  testId={`${PREFIX}-day`}
                />
              </div>
            </div>

            <TextField
              type="date"
              label="Hire date"
              min={MIN_HIRE_DATE}
              max={maxHireDate(today)}
              value={draft.hireDate}
              onChange={(event) => {
                setError(null);
                setDraft((prev) => ({ ...prev, hireDate: event.target.value }));
              }}
              disabled={submitting}
              error={hireDateError(draft.hireDate, today) !== null}
              testId={`${PREFIX}-hire-date`}
            />

            <p className="font-sans text-s13 leading-h15 tracking-normal text-text-tertiary">
              {storedBirthday === null
                ? 'Leave a field unset to store nothing for it.'
                : `Currently on file: ${storedBirthday}.`}{' '}
              Setting a field back to “Not set” (or clearing the hire date) removes it.
            </p>

            {draftError !== null && (
              <p
                className="font-sans text-s14 font-w500 tracking-normal text-danger-700"
                {...testIdProp(`${PREFIX}-draft-error`)}
              >
                {draftError}
              </p>
            )}

            {error !== null && (
              <p
                className="font-sans text-s14 font-w500 tracking-normal text-danger-700"
                {...testIdProp(`${PREFIX}-error`)}
              >
                {error}
              </p>
            )}

            <div className="mt-8 flex items-center justify-end gap-8">
              <Dialog.Close
                render={<Button appearance="saas" tone="secondary" size="md" />}
                {...testIdProp(`${PREFIX}-cancel`)}
              >
                Cancel
              </Dialog.Close>
              <Button
                appearance="saas"
                tone="primary"
                size="md"
                loading={submitting}
                disabled={!canSave}
                onClick={submit}
                testId={`${PREFIX}-save`}
              >
                Save dates
              </Button>
            </div>
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
